import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Api from "../../api/endPoints";

// The nsync asset-template profile id every device list/dashboard query is
// scoped to. It used to be hardcoded as 64 on each page; it is now fetched once
// per session and read from here, so a backend change doesn't need a rebuild.

// The endpoint answers with a bare number (64), but tolerate the object shapes
// a backend might wrap it in rather than silently resolving to NaN.
const normalizeProfileId = (data) => {
    const raw = typeof data === "object" && data !== null
        ? data.profileId ?? data.nsyncProfileId ?? data.id ?? data.data
        : data;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
};

export const fetchAssetProfile = createAsyncThunk(
    "assetProfile/fetch",
    async (_, { rejectWithValue }) => {
        const { status, data } = await Api.getNsyncProfile();
        if (status !== 200) return rejectWithValue(`Request failed with status ${status}`);
        const profileId = normalizeProfileId(data);
        if (profileId == null) return rejectWithValue("Profile response was not a number");
        return profileId;
    },
    {
        // Every page mounts wanting the profile id. Without this the first few
        // mounts would each fire their own request — `idle` is the only state
        // that lets one through, so the fetch happens exactly once per session.
        condition: (_, { getState }) => getState().assetProfile.status === "idle",
    },
);

const initialState = {
    profileId: null,
    status: "idle", // idle | loading | succeeded | failed
    error: null,
};

const assetProfileSlice = createSlice({
    name: "assetProfile",
    initialState,
    reducers: {
        // Lets a failed load be retried — `condition` above only re-runs the
        // thunk once the status is back to idle.
        resetAssetProfile: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAssetProfile.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchAssetProfile.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.profileId = action.payload;
            })
            .addCase(fetchAssetProfile.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload ?? action.error?.message ?? "Couldn't load asset profile";
            });
    },
});

export const { resetAssetProfile } = assetProfileSlice.actions;

export const selectAssetProfileId = (state) => state.assetProfile.profileId;
export const selectAssetProfileStatus = (state) => state.assetProfile.status;
export const selectAssetProfileError = (state) => state.assetProfile.error;

export default assetProfileSlice.reducer;
